import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Row, Col, Card, Button, Form, Table, Badge, Alert } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import PageLayout from '../../layouts/PageLayout';
import { useBranch, useUser } from '../../hooks';
import { shiftTemplateAPI, taskAPI, taskDurationAPI } from '../../services';
import { toast } from 'react-toastify';
import TaskFormModal from '../../components/modals/TaskFormModal';
import TaskShiftLinksModal from '../../components/modals/TaskShiftLinksModal';
import TaskDurationHistoryModal from '../../components/modals/TaskDurationHistoryModal';
import TaskDetailModal from '../../components/modals/TaskDetailModal';

/** #142526 — nhãn tiếng Việt của khung giờ trong ca (đầu/giữa/cuối ca) */
const SHIFT_SEGMENT_LABEL = {
  START: 'Đầu ca',
  MIDDLE: 'Giữa ca',
  END: 'Cuối ca',
};

export default function TaskConfigPage() {
  const { branches, selectedBranch } = useBranch();
  const { user } = useUser();
  // Quyền sửa bám đúng quyền của POST /shift-templates/:id/tasks — không nới rộng.
  const canEditLinks = user?.role === 'ADMIN' || user?.role === 'MANAGER';

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('active');
  const [urgentFilter, setUrgentFilter] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [branchFilter, setBranchFilter] = useState(() => selectedBranch?.id || '');

  const [showModal, setShowModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);

  // #110145 — cột "Ca áp dụng": các ca đang gắn cho từng task (map task_id → mảng)
  const [shiftLinks, setShiftLinks] = useState({});
  const [linksLoading, setLinksLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [linkTask, setLinkTask] = useState(null);

  // #142431 — cột "TB thực tế": trung bình do chính nhân viên khảo sát (map task_id → stats)
  const [durationStats, setDurationStats] = useState({});
  const [durationTask, setDurationTask] = useState(null);

  // #015517 — popup CHỈ XEM thông tin task + link riêng `/tasks?id=<taskId>`.
  // `id` nằm trên URL nên: (1) mở link là hiện ngay popup đúng task (kể cả sau khi đăng nhập, nhờ
  // `RequireAuth` giữ `?next=`); (2) nút "Sao chép" chỉ cần đọc lại chính URL này.
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTaskId = searchParams.get('id') || '';
  const [showDetail, setShowDetail] = useState(false);
  const [detailTaskId, setDetailTaskId] = useState('');
  // Bump key để popup tải lại thông tin MỚI sau khi sửa xong.
  const [detailRefreshKey, setDetailRefreshKey] = useState(0);

  useEffect(() => {
    setBranchFilter(selectedBranch?.id || '');
  }, [selectedBranch]);

  // Mở link `/tasks?id=<taskId>` ⇒ mở popup đúng task đó. Link cũ/đổi id cũng bám theo.
  useEffect(() => {
    if (urlTaskId) {
      setDetailTaskId(urlTaskId);
      setShowDetail(true);
    }
  }, [urlTaskId]);

  useEffect(() => {
    loadTasks();
  }, [statusFilter]);

  const loadTasks = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = { page: 1, limit: 200 };
      if (statusFilter === 'active') params.is_active = true;
      else if (statusFilter === 'inactive') params.is_active = false;
      const res = await taskAPI.getTasks(params);
      const list = res.data || res;
      setTasks(list);
      loadShiftLinks(list);
      loadDurationStats(list);
    } catch (err) {
      console.error('Error loading tasks:', err);
      setError('Không thể tải danh sách task');
    } finally {
      setLoading(false);
    }
  };

  // #142431 — trung bình thời gian thực tế cho TẤT CẢ task hiển thị, chia lô như cột "Ca áp dụng".
  const loadDurationStats = useCallback(async (taskList) => {
    const ids = (taskList || []).map((t) => t.id).filter(Boolean);
    if (ids.length === 0) {
      setDurationStats({});
      return;
    }
    try {
      const CHUNK = 50;
      const chunks = [];
      for (let i = 0; i < ids.length; i += CHUNK) chunks.push(ids.slice(i, i + CHUNK));
      const results = await Promise.all(chunks.map((chunk) => taskDurationAPI.getStats(chunk)));
      setDurationStats(Object.assign({}, ...results.map((r) => r?.data || r || {})));
    } catch (err) {
      console.error('Error loading task duration stats:', err);
      // Cột này hỏng thì chỉ cột này hiện "—", không chặn cả bảng task.
      setDurationStats({});
    }
  }, []);

  // #110145 — lấy các ca đang gắn cho TẤT CẢ task hiển thị trong MỘT lượt gọi
  // (chống N+1: không gọi 1 lượt cho mỗi ca / mỗi task).
  const loadShiftLinks = useCallback(async (taskList) => {
    const ids = (taskList || []).map((t) => t.id).filter(Boolean);
    if (ids.length === 0) {
      setShiftLinks({});
      return;
    }
    try {
      setLinksLoading(true);
      // Chia lô: 200 task = ~7,4KB id trên query string, chạm trần độ dài URL của
      // nhiều proxy. 50 id/lượt vẫn xa N+1 (1 task = 1 lượt) mà URL luôn an toàn.
      const CHUNK = 50;
      const chunks = [];
      for (let i = 0; i < ids.length; i += CHUNK) chunks.push(ids.slice(i, i + CHUNK));
      const results = await Promise.all(
        chunks.map((chunk) => shiftTemplateAPI.getTaskShiftLinks({ task_ids: chunk })),
      );
      setShiftLinks(Object.assign({}, ...results.map((r) => r?.data || {})));
    } catch (err) {
      console.error('Error loading task-shift links:', err);
      // Không chặn cả bảng vì riêng cột này hỏng — cột sẽ hiện dấu "?".
      setShiftLinks({});
    } finally {
      setLinksLoading(false);
    }
  }, []);

  // Danh sách ca của MỌI chi nhánh (task có thể là task chung, gắn được cho ca
  // bất kỳ chi nhánh nào) — kèm tên chi nhánh để chọn trong modal khỏi nhầm.
  const loadTemplates = useCallback(async () => {
    try {
      const res = await shiftTemplateAPI.getShiftTemplates({ page: 1, limit: 500 });
      setTemplates(res?.data || res || []);
    } catch (err) {
      console.error('Error loading shift templates:', err);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const templatesWithBranch = useMemo(
    () =>
      (templates || []).map((t) => ({
        ...t,
        branch_name: branches.find((b) => b.id === t.branch_id)?.name || null,
      })),
    [templates, branches],
  );

  const handleLinksChanged = useCallback(() => {
    loadShiftLinks(tasks);
  }, [loadShiftLinks, tasks]);

  const openCreate = () => {
    setEditingTask(null);
    setShowModal(true);
  };

  const openEdit = (task) => {
    setEditingTask(task);
    setShowModal(true);
  };

  // Cập nhật `?id=` trên URL mà GIỮ nguyên các tham số khác (vd `branch_id` toàn cục).
  const writeTaskIdParam = (taskId, replace) => {
    const next = new URLSearchParams(searchParams);
    if (taskId) next.set('id', taskId);
    else next.delete('id');
    setSearchParams(next, { replace: !!replace });
  };

  // Bấm 1 dòng task → mở popup chỉ xem (KHÔNG nhảy thẳng vào sửa như trước).
  const openDetail = (task) => {
    if (!task?.id) return;
    setDetailTaskId(task.id);
    setShowDetail(true);
    writeTaskIdParam(task.id, false);
  };

  const closeDetail = () => {
    setShowDetail(false);
    setDetailTaskId('');
    writeTaskIdParam('', true);
  };

  // Trong popup bấm "Sửa" → mở ĐÚNG modal sửa đang có (TaskFormModal), không dựng màn sửa thứ hai.
  const handleEditFromDetail = (task) => {
    if (!task?.id) return;
    setEditingTask(task);
    setShowModal(true);
  };

  const handleTaskSaved = async () => {
    await loadTasks();
    // Popup đang mở (nếu có) tải lại để thấy thông tin vừa sửa.
    setDetailRefreshKey((k) => k + 1);
  };

  const handleToggleActive = async (task) => {
    try {
      await taskAPI.updateTask(task.id, { is_active: !task.is_active });
      await loadTasks();
    } catch (err) {
      console.error('Error toggling task:', err);
      toast.error('Không thể đổi trạng thái task');
    }
  };

  const handleDelete = async (task) => {
    if (!window.confirm('Bạn có chắc muốn xóa task này?')) return;
    try {
      await taskAPI.deleteTask(task.id);
      toast.success('Xóa task thành công');
      await loadTasks();
    } catch (err) {
      console.error('Error deleting task:', err);
      toast.error('Không thể xóa task');
    }
  };

  const DOW_LABELS = ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'];

  return (
    <PageLayout>
      <div className="mc-page">
        <div className="mc-page-header">
          <div className="mc-page-header-title">
            <h1>Cấu hình Task</h1>
            <p>Quản lý danh mục công việc được dùng trong ca</p>
          </div>
          <div className="mc-page-header-actions">
            <Button variant="primary" onClick={openCreate} disabled={loading} data-testid="tc-them-task">
              Thêm task
            </Button>
          </div>
        </div>

        <Card className="mc-card">
          <Card.Header>
            <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap">
              <h5 className="mb-0 flex-shrink-0">Danh sách task</h5>
              <Form.Control
                type="text"
                size="sm"
                placeholder="Tìm theo tên, danh mục..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ maxWidth: 220 }}
              />
              <div className="btn-group flex-shrink-0" role="group">
                <Button
                  variant={statusFilter === 'all' ? 'primary' : 'outline-primary'}
                  size="sm"
                  onClick={() => setStatusFilter('all')}
                >
                  Tất cả
                </Button>
                <Button
                  variant={statusFilter === 'active' ? 'primary' : 'outline-primary'}
                  size="sm"
                  onClick={() => setStatusFilter('active')}
                >
                  Hoạt động
                </Button>
                <Button
                  variant={statusFilter === 'inactive' ? 'primary' : 'outline-primary'}
                  size="sm"
                  onClick={() => setStatusFilter('inactive')}
                >
                  Tạm dừng
                </Button>
              </div>
              <Button
                variant={urgentFilter ? 'danger' : 'outline-danger'}
                size="sm"
                className="flex-shrink-0"
                onClick={() => setUrgentFilter((prev) => !prev)}
              >
                🚨 Khẩn cấp
              </Button>
            </div>
          </Card.Header>

          <Card.Body>
            {error && <Alert variant="danger">{error}</Alert>}
            {loading ? (
              <div className="text-center py-4">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : (
              /* #160259 — bảng 17 cột (#142526 thêm cột Khung giờ), khổ điện thoại phải cuộn ngang: bọc bằng khung
                 `mc-table-sticky` (lớp dùng chung ở `_table.scss`) để cột "Tên" và hàng
                 tiêu đề luôn đứng yên. Không dùng prop `responsive` của react-bootstrap
                 nữa vì prop đó tự dựng khung cuộn riêng, không cho gắn class vào đó. */
              <div className="mc-table-sticky">
                <Table hover>
                  <thead>
                    <tr>
                      <th>Tên</th>
                      <th>Loại</th>
                      {/* #213032 — việc nào đang nghiệm thu 2 lần (đầu ca + cuối ca) */}
                      <th>Nghiệm thu</th>
                      <th>Danh mục</th>
                      <th>Điểm</th>
                      <th>Est. phút</th>
                      <th>Khung giờ</th>
                      <th>TB thực tế</th>
                      <th>Chi nhánh</th>
                      <th>Ca áp dụng</th>
                      <th>Ngày áp dụng</th>
                      <th>Giám sát chéo</th>
                      <th>Bằng chứng</th>
                      <th>Thông báo Tele</th>
                      <th>Bắt buộc CO</th>
                      <th>Khẩn cấp</th>
                      <th>Trạng thái</th>
                      <th>Thao tác</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tasks
                      .filter((t) => {
                        if (branchFilter === '__global__' && t.branch_id) return false;
                        if (branchFilter && branchFilter !== '__global__' && t.branch_id && t.branch_id !== branchFilter) return false;
                        if (urgentFilter && !t.is_urgent) return false;
                        if (searchTerm.trim()) {
                          const s = searchTerm.toLowerCase();
                          if (!t.name?.toLowerCase().includes(s) && !t.category?.toLowerCase().includes(s)) return false;
                        }
                        return true;
                      })
                      .map((t) => {
                        const points = t.points ?? (t.reward_point || 0) - (t.penalty_point || 0);
                        const hasPoints = t.has_points ?? (t.reward_point !== undefined || t.penalty_point !== undefined);
                        const crossReview = [];
                        if (t.cross_review_previous_shift) crossReview.push('Ca trước');
                        if (t.cross_review_same_shift) crossReview.push('Cùng ca');
                        const taskType = t.task_type || 'SUB_TASK';
                        const dow = t.days_of_week || [];

                        return (
                          <tr
                            key={t.id}
                            onClick={() => openDetail(t)}
                            style={{ cursor: 'pointer' }}
                            title="Xem thông tin task"
                            data-testid={`task-row-${t.id}`}
                          >
                            <td>{t.name}</td>
                            <td>
                              {taskType === 'MAIN_TASK' ? (
                                <Badge bg="primary">Việc trực ca</Badge>
                              ) : (
                                <Badge bg="secondary">Việc phải nghiệm thu</Badge>
                              )}
                            </td>
                            {/* #213032 — hiện rõ việc nào đang nghiệm thu 2 lần (đầu ca + cuối ca) */}
                            <td>
                              {t.dual_review ? (
                                <Badge bg="warning" text="dark" data-testid={`dual-review-${t.id}`}>
                                  2 lần (Đầu + Cuối ca)
                                </Badge>
                              ) : (
                                <span className="text-muted">1 lần</span>
                              )}
                            </td>
                            <td>{t.category}</td>
                            <td>
                              {hasPoints ? (
                                <Badge bg={points >= 0 ? 'success' : 'danger'}>
                                  {points >= 0 ? '+' : ''}{points}
                                </Badge>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            <td>
                              {t.estimated_minutes != null ? (
                                <span>{t.estimated_minutes}p</span>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            {/* #142526 — khung giờ trong ca phải làm việc này (đầu/giữa/cuối ca) */}
                            <td>
                              {t.shift_segment ? (
                                <Badge bg="info">{SHIFT_SEGMENT_LABEL[t.shift_segment] || t.shift_segment}</Badge>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            {/* #142431 — TB do nhân viên tự khảo sát, ĐỨNG CẠNH cột khai tay ở trên
                                (không thay, không xoá cột cũ). Chưa đủ mẫu thì nói rõ còn thiếu bao
                                nhiêu, KHÔNG hiện 0 phút. */}
                            <td style={{ minWidth: 150 }} data-testid={`duration-cell-${t.id}`}>
                              {(() => {
                                const st = durationStats[t.id];
                                if (!st || st.status === 'NO_DATA')
                                  return <span className="text-muted small">Chưa có dữ liệu khảo sát</span>;
                                if (st.status !== 'OK')
                                  return (
                                    <span className="text-muted small">
                                      Chưa đủ mẫu ({st.valid_samples}/{st.min_samples})
                                    </span>
                                  );
                                return (
                                  <span>
                                    <Badge bg="info">{st.average_minutes}p</Badge>{' '}
                                    <span className="text-muted small">({st.total_samples} mẫu)</span>
                                  </span>
                                );
                              })()}
                              <div>
                                <Button
                                  size="sm"
                                  variant="link"
                                  className="p-0 text-decoration-none"
                                  data-testid={`duration-history-${t.id}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setDurationTask(t);
                                  }}
                                >
                                  Xem lịch sử
                                </Button>
                              </div>
                            </td>
                            <td>
                              {t.branch_id ? (
                                branches.find((b) => b.id === t.branch_id)?.name || t.branch_id
                              ) : (
                                <Badge bg="secondary">Tất cả</Badge>
                              )}
                            </td>
                            <td style={{ minWidth: 190 }}>
                              <div className="d-flex flex-wrap align-items-center gap-1">
                                {linksLoading && !shiftLinks[t.id] ? (
                                  <span className="text-muted small">Đang tải...</span>
                                ) : (shiftLinks[t.id] || []).length === 0 ? (
                                  <Badge bg="warning" text="dark">Chưa gắn ca nào</Badge>
                                ) : (
                                  shiftLinks[t.id].map((link) => (
                                    <Badge
                                      key={link.id}
                                      bg={link.template_is_active ? 'primary' : 'secondary'}
                                      title={`${link.template_name}${link.branch_name ? ' · ' + link.branch_name : ''}`}
                                    >
                                      {link.template_name}
                                      {link.branch_name ? ` · ${link.branch_name}` : ''}
                                    </Badge>
                                  ))
                                )}
                                <Button
                                  size="sm"
                                  variant="link"
                                  className="p-0 ms-1 text-decoration-none"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setLinkTask(t);
                                  }}
                                >
                                  {canEditLinks ? 'Sửa ca' : 'Xem ca'}
                                </Button>
                              </div>
                            </td>
                            <td>
                              {dow.length === 0 ? (
                                <span className="text-muted">Mọi ngày</span>
                              ) : (
                                <div className="d-flex flex-wrap gap-1">
                                  {dow.map((d) => (
                                    <Badge key={d} bg="info" style={{ fontSize: '10px' }}>
                                      {DOW_LABELS[d]}
                                    </Badge>
                                  ))}
                                </div>
                              )}
                            </td>
                            <td>
                              {crossReview.length > 0 ? (
                                <div className="d-flex flex-column gap-1">
                                  {crossReview.map((item, idx) => (
                                    <Badge key={idx} bg="info" className="w-fit">{item}</Badge>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            <td>
                              {t.requires_evidence ? (
                                <Badge bg="warning">Bắt buộc</Badge>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            <td>
                              {t.notify_telegram ? (
                                <Badge bg="info">Bật</Badge>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            <td>
                              {t.required_for_checkout ? (
                                <Badge bg="warning">Bắt buộc</Badge>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            <td>
                              {t.is_urgent ? (
                                <Badge bg="danger">Khẩn cấp</Badge>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            <td>
                              <Badge bg={t.is_active ? 'success' : 'secondary'}>
                                {t.is_active ? 'Hoạt động' : 'Tạm dừng'}
                              </Badge>
                            </td>
                            <td>
                              <div className="d-flex gap-2">
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openEdit(t);
                                  }}
                                >
                                  Sửa
                                </Button>
                                <Button
                                  size="sm"
                                  variant={t.is_active ? 'outline-warning' : 'outline-success'}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleToggleActive(t);
                                  }}
                                >
                                  {t.is_active ? 'Tạm dừng' : 'Kích hoạt'}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline-danger"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDelete(t);
                                  }}
                                >
                                  Xóa
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
        </Card>

        <TaskFormModal
          show={showModal}
          onHide={() => setShowModal(false)}
          task={editingTask}
          onSave={handleTaskSaved}
        />

        {/* #015517 — popup chỉ xem; nút Sửa trong popup mở lại TaskFormModal ở trên. */}
        <TaskDetailModal
          show={showDetail}
          taskId={detailTaskId}
          refreshKey={detailRefreshKey}
          branches={branches}
          canEdit={canEditLinks}
          onHide={closeDetail}
          onEdit={handleEditFromDetail}
        />

        <TaskShiftLinksModal
          show={!!linkTask}
          onHide={() => setLinkTask(null)}
          task={linkTask}
          templates={templatesWithBranch}
          // #221348 — danh sách ô tick bám chi nhánh đang chọn của màn này
          branchId={branchFilter}
          branchName={branches.find((b) => b.id === branchFilter)?.name || ''}
          canEdit={canEditLinks}
          onChanged={handleLinksChanged}
        />

        {durationTask && (
          <TaskDurationHistoryModal
            task={durationTask}
            onHide={() => setDurationTask(null)}
          />
        )}
      </div>
    </PageLayout>
  );
}
